import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import SubNav from '../../components/SubNav/SubNav';
import { useAuth } from '../../contexts/AuthContext';
import { isOwnerToolsUser } from '../../utils/ownerTools';
import { llmService } from '../../services/llmService';
import { useVisualViewportLock } from '../../hooks/useVisualViewportLock';
import '../../styles/Dashboard.css';
import './Translate.css';

const Translate = () => {
  const { user, loading } = useAuth();
  const [english, setEnglish] = useState('');
  const [spanish, setSpanish] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [mobilePane, setMobilePane] = useState('en');

  useVisualViewportLock();

  if (loading) {
    return (
      <div className="dashboard-page">
        <Header />
        <SubNav />
        <div className="dashboard-container">Loading...</div>
      </div>
    );
  }

  if (!isOwnerToolsUser(user)) {
    return <Navigate to="/" replace />;
  }

  const copyBox = async (value, which) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      setError('Could not copy');
    }
  };

  const handleTranslate = async (event) => {
    event.preventDefault();
    const text = english.trim();
    if (!text || busy) return;
    setBusy(true);
    setError('');
    try {
      const data = await llmService.translate(text);
      setSpanish(data.translation || '');
      setMobilePane('es');
    } catch (err) {
      setError(err.message || 'Translation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dashboard-page translate-page owner-tool-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Translate | NowTarot AI</title>
      </Helmet>
      <Header />
      <SubNav />
      <div className="dashboard-container">
        {error && <div className="alert alert-error">{error}</div>}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title">English → Mexican Spanish</h3>
          </div>
          <div className="translate-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mobilePane === 'en'}
              className={mobilePane === 'en' ? 'active' : ''}
              onClick={() => setMobilePane('en')}
            >
              English
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobilePane === 'es'}
              className={mobilePane === 'es' ? 'active' : ''}
              onClick={() => setMobilePane('es')}
            >
              Español
            </button>
          </div>
          <form className={`translate-grid translate-pane-${mobilePane}`} onSubmit={handleTranslate}>
            <label className="translate-box">
              <div className="translate-box-top">
                <span>English</span>
                <button type="button" onClick={() => copyBox(english, 'en')} disabled={!english}>
                  {copied === 'en' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <textarea
                value={english}
                onChange={(e) => setEnglish(e.target.value)}
                placeholder="Type English here…"
                rows={8}
                enterKeyHint="done"
              />
            </label>
            <label className="translate-box">
              <div className="translate-box-top">
                <span>Español mexicano</span>
                <button type="button" onClick={() => copyBox(spanish, 'es')} disabled={!spanish}>
                  {copied === 'es' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <textarea
                value={spanish}
                readOnly
                placeholder="Translation appears here…"
                rows={8}
              />
            </label>
            <button type="submit" className="btn btn-primary translate-submit" disabled={busy || !english.trim()}>
              {busy ? 'Translating…' : 'Translate'}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Translate;
